/**
 * Smart startup sequence to avoid 401/429 errors
 * Only calls public endpoints before login, protected endpoints after login
 */

import api, { getWithWarmup } from './api.js';
import { queueApiCall, PRIORITY } from './init-queue.js';
import { fetchHealth } from './health.js';

class StartupManager {
  constructor() {
    this.publicConfigLoaded = false;
    this.healthChecked = false;
    this.isAuthenticated = false;
    this.config = null;
  }

  // Phase 1: Public initialization (no auth required)
  async initPublic() {
    console.log('🚀 Starting public initialization...');
    
    try {
      // Health check (unified GET /health) with cold start tolerance
      await queueApiCall(
        () => fetchHealth(),
        PRIORITY.CRITICAL
      );
      this.healthChecked = true;
      console.log('✅ Health check passed');
    } catch (error) {
      console.warn('⚠️ Health check failed:', error.message);
    }

    try {
      // Public config (maintenance, flags) with cold start tolerance
      this.config = await queueApiCall(
        () => getWithWarmup(api, '/public/config'),
        PRIORITY.CRITICAL
      );
      this.publicConfigLoaded = true;
      console.log('✅ Public config loaded:', this.config);
    } catch (error) {
      console.warn('⚠️ Public config failed:', error.message);
      // Fallback config
      this.config = {
        maintenance: false,
        message: null,
        flags: {
          economyCharts: true,
          crafting: true,
          travel: true,
          missions: true,
          inventory: true
        }
      };
    }

    return this.config;
  }

  // Phase 2: Protected initialization (after login)
  async initProtected(store) {
    if (!this.isAuthenticated) {
      console.warn('❌ Cannot init protected endpoints - not authenticated');
      return;
    }

    console.log('🔒 Starting protected initialization...');

    try {
      // Load protected data sequentially with delays to avoid rate limiting
      const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

      // 1. Active mission/travel status (high priority)
      if (this.config?.flags?.missions) {
        try {
          await queueApiCall(
            () => store.missions?.checkActiveRun(),
            PRIORITY.HIGH
          );
          console.log('✅ Active missions loaded');
        } catch (error) {
          console.warn('⚠️ Active missions failed:', error.message);
        }
        await delay(150);
      }

      if (this.config?.flags?.travel) {
        try {
          await queueApiCall(
            () => store.travel?.checkActiveTravel(),
            PRIORITY.HIGH
          );
          console.log('✅ Active travel loaded');
        } catch (error) {
          console.warn('⚠️ Active travel failed:', error.message);
        }
        await delay(150);
      }

      // 2. Available data (normal priority)
      if (this.config?.flags?.missions) {
        try {
          await queueApiCall(
            () => store.missions?.loadMissions(),
            PRIORITY.NORMAL
          );
          console.log('✅ Missions list loaded');
        } catch (error) {
          console.warn('⚠️ Missions list failed:', error.message);
        }
        await delay(150);
      }

      if (this.config?.flags?.travel) {
        try {
          await queueApiCall(
            () => store.travel?.loadDestinations(),
            PRIORITY.NORMAL
          );
          console.log('✅ Travel destinations loaded');
        } catch (error) {
          console.warn('⚠️ Travel destinations failed:', error.message);
        }
        await delay(150);
      }

      // 3. Economy and other data (low priority)
      if (this.config?.flags?.economyCharts) {
        try {
          await queueApiCall(
            () => store.economy?.loadStats(),
            PRIORITY.LOW
          );
          console.log('✅ Economy data loaded');
        } catch (error) {
          console.warn('⚠️ Economy data failed:', error.message);
        }
      }

      console.log('🎉 Protected initialization complete');
      
    } catch (error) {
      console.error('❌ Protected initialization failed:', error);
    }
  }

  // Set authentication status
  setAuthenticated(authenticated) {
    this.isAuthenticated = authenticated;
    console.log(`🔐 Authentication status: ${authenticated ? 'logged in' : 'logged out'}`);
  }

  // Check if feature is enabled
  isFeatureEnabled(feature) {
    return this.config?.flags?.[feature] ?? true;
  }

  // Check maintenance mode
  isMaintenanceMode() {
    return this.config?.maintenance ?? false;
  }

  // Get maintenance message
  getMaintenanceMessage() {
    return this.config?.message || 'System maintenance in progress';
  }
}

// Export singleton instance
export const startupManager = new StartupManager();

// Helper functions
export function hasToken() {
  return !!localStorage.getItem('auth:token');
}

export function isAuthenticatedComponent(component) {
  return hasToken() && startupManager.isAuthenticated;
}

export function shouldLoadProtectedData(feature) {
  return isAuthenticatedComponent() && startupManager.isFeatureEnabled(feature);
}